import React, { useCallback, useEffect, useRef } from 'react';
import { Platform, SafeAreaView, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { isPhone, isWeb, moderateScale } from '@helpers';
import ColorsWrapper from '@styles/colors';
import { scale } from '@components/frame';
import {
  AUTH_PHONE_BOTTOM_PADDING,
  AUTH_PHONE_HORIZONTAL_PADDING,
  AUTH_TABLET_WRAPPER_WIDTH,
  AUTH_TALL_PHONE_HEIGHT_THRESHOLD,
  AUTH_TALL_PHONE_TOP_MARGIN,
  AUTH_WEB_CARD_PADDING_HORIZONTAL,
  AUTH_WEB_CARD_PADDING_VERTICAL,
  AUTH_WEB_CARD_TOP_BIAS,
  AUTH_WEB_CARD_WIDTH,
  AUTH_WRAPPER_TOP_PADDING,
} from './constants';
import useKeyboardHeight from './useKeyboardHeight';
import useKeyboardOverlap from './useKeyboardOverlap';

const SYSTEM_BG = 'Background / Light / System';
const SYSTEM_BG_NO_SPACES = 'Background/Light/System';

const WebLayout = ({ children, C }) => (
  <View
    style={{
      width: '100%',
      height: '100%',
      justifyContent: 'center',
      alignContent: 'center',
      alignItems: 'center',
      backgroundColor: C(SYSTEM_BG_NO_SPACES),
    }}
  >
    <View
      style={{
        width: AUTH_WEB_CARD_WIDTH,
        borderColor: '#CCCCCC',
        borderWidth: StyleSheet.hairlineWidth,
        borderRadius: 5,
        paddingHorizontal: AUTH_WEB_CARD_PADDING_HORIZONTAL,
        paddingVertical: AUTH_WEB_CARD_PADDING_VERTICAL,
        marginTop: AUTH_WEB_CARD_TOP_BIAS,
      }}
    >
      {children}
    </View>
  </View>
);

/**
 * SafeArea + screen-edge handling. The keyboard-driven `paddingBottom` is the
 * fallback for non-`keyboardAvoid` consumers (e.g. Login) where the screen
 * doesn't need scrollable form behavior.
 *
 * `KeyboardAvoidingView` is intentionally NOT used: in Android 15 edge-to-edge
 * mode its internal frame events frequently don't fire and the wrapper does
 * nothing.
 */
const SafeAreaWithKeyboard = ({ children, C, centered, keyboardAvoid, keyboardOverlap }) => {
  const { top } = useSafeAreaInsets();

  const shouldApplyAndroidTopInset = Platform.OS === 'android' && isPhone;
  const androidTopInset = shouldApplyAndroidTopInset ? (centered ? AUTH_PHONE_BOTTOM_PADDING : top) : null;

  return (
    <SafeAreaView
      style={[
        { backgroundColor: C(SYSTEM_BG), flex: 1 },
        shouldApplyAndroidTopInset && { paddingTop: androidTopInset },
        keyboardAvoid && keyboardOverlap > 0 && { paddingBottom: keyboardOverlap },
      ]}
    >
      {children}
    </SafeAreaView>
  );
};

/**
 * Mobile layout: SafeAreaView → MainContainer → CenterBlock → MainWrapper.
 *
 * `keyboardAvoid` consumers wrap in a ScrollView so a tall form (e.g.
 * CreateAccount: logo + 3 inputs + button ≈ 460dp) on a tablet in landscape
 * (≈ 720dp tall, keyboard ≈ 430dp) can never end up cut off above the
 * visible area — the previous paddingBottom + recentered approach shifts
 * content by half the overlap, leaving the focused input clipped.
 */
const MobileLayout = ({ children, C, centered, keyboardAvoid, offset, padding, paddingHorizontal }) => {
  const scrollRef = useRef(null);
  const scrollYRef = useRef(0);
  // Tablet (any OS) and iPhone: ScrollView path — iOS handles focus-into-view
  // via `automaticallyAdjustKeyboardInsets` which also gracefully scrolls when
  // the form is taller than `window − keyboard` (createAccount on a notched
  // iPhone in portrait). Android phone: a flex-end paddingBottom shift lifts
  // the small form entirely so every input stays visible regardless of focus.
  const isAndroidPhone = Platform.OS === 'android' && isPhone;
  const keyboardOverlap = useKeyboardOverlap(keyboardAvoid && !isAndroidPhone);
  const phoneKbHeight = useKeyboardHeight(keyboardAvoid && isAndroidPhone);
  const { height: windowHeight } = useWindowDimensions();

  // iOS handles focus-into-view via `automaticallyAdjustKeyboardInsets`;
  // Android's `adjustResize` is unreliable in edge-to-edge so we drive scroll
  // manually whenever the overlap changes. Phone path renders no ScrollView,
  // so `scrollRef.current` stays null and the guard short-circuits there.
  useEffect(() => {
    if (!keyboardAvoid || Platform.OS !== 'android' || !scrollRef.current) return;
    const targetY = keyboardOverlap > 0 ? scrollYRef.current + keyboardOverlap : 0;
    scrollRef.current.scrollTo({ y: targetY, animated: true });
  }, [keyboardAvoid, keyboardOverlap]);

  const handleScroll = useCallback((e) => {
    scrollYRef.current = e.nativeEvent.contentOffset.y;
  }, []);

  const isTallPhone = isPhone && windowHeight > AUTH_TALL_PHONE_HEIGHT_THRESHOLD;
  const phoneKeyboardUp = keyboardAvoid && isAndroidPhone && phoneKbHeight > 0;
  const tabletKeyboardUp = keyboardAvoid && !isPhone && keyboardOverlap > 0;
  const isTabletKeyboardDown = !isPhone && !tabletKeyboardUp;

  const innerLayout = (
    <View
      style={[
        styles.mainContainer,
        { flex: 1 },
        isTabletKeyboardDown && { marginTop: moderateScale(offset) },
        // Phone: anchor to bottom of the (shrunk) safe area so the
        // SafeAreaView's paddingBottom translates 1:1 into upward shift,
        // bringing the whole form (incl. Ready button) above the keyboard.
        phoneKeyboardUp && { justifyContent: 'flex-end' },
      ]}
    >
      <View
        style={[
          styles.centerBlock,
          isTabletKeyboardDown
            ? { justifyContent: 'center', marginTop: moderateScale(padding), height: '100%' }
            : { paddingBottom: moderateScale(AUTH_PHONE_BOTTOM_PADDING) },
          isTallPhone && !phoneKeyboardUp && { marginTop: moderateScale(AUTH_TALL_PHONE_TOP_MARGIN) },
          { backgroundColor: C(SYSTEM_BG) },
        ]}
      >
        <View
          style={[
            styles.mainWrapper,
            { backgroundColor: C(SYSTEM_BG), width: scale(AUTH_TABLET_WRAPPER_WIDTH) },
            isPhone && {
              paddingHorizontal: paddingHorizontal ? scale(AUTH_PHONE_HORIZONTAL_PADDING) : 0,
              width: '100%',
            },
          ]}
        >
          {children}
        </View>
      </View>
    </View>
  );

  if (keyboardAvoid && !isAndroidPhone) {
    return (
      <SafeAreaWithKeyboard {...{ C, centered }}>
        <ScrollView
          ref={scrollRef}
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          automaticallyAdjustKeyboardInsets
          keyboardDismissMode="interactive"
          onScroll={handleScroll}
          scrollEventThrottle={100}
        >
          {innerLayout}
        </ScrollView>
      </SafeAreaWithKeyboard>
    );
  }

  return (
    <SafeAreaWithKeyboard
      C={C}
      centered={centered}
      keyboardAvoid={keyboardAvoid}
      keyboardOverlap={isAndroidPhone ? phoneKbHeight : keyboardOverlap}
    >
      {innerLayout}
    </SafeAreaWithKeyboard>
  );
};

/**
 * Top-level layout used by every auth screen.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {number} [props.offset=0] Tablet-only marginTop nudge for `mainContainer`. Ignored on phone and while keyboard is up.
 * @param {number} [props.padding=0] Tablet-only marginTop nudge for `centerBlock`. Ignored on phone and while keyboard is up.
 * @param {boolean} [props.paddingHorizontal=true] Apply horizontal padding inside `mainWrapper` on phone.
 * @param {boolean} [props.centered] When true, treat the screen as a centered layout (web) or skip the Android safe-area inset handling on phone.
 * @param {boolean} [props.keyboardAvoid=false] Wrap in a ScrollView and reserve keyboard space; required on screens with editable inputs that risk overlapping the keyboard.
 * @param {(key: string) => string} props.C Color resolver injected by ColorsWrapper.
 */
const TablerWrapper = ({
  children,
  offset = 0,
  padding = 0,
  paddingHorizontal = true,
  centered,
  keyboardAvoid = false,
  C,
}) => {
  if (isWeb) return <WebLayout {...{ C }}>{children}</WebLayout>;

  return (
    <MobileLayout {...{ C, centered, keyboardAvoid, offset, padding, paddingHorizontal }}>
      {children}
    </MobileLayout>
  );
};

export default ColorsWrapper(TablerWrapper);

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'column',
  },
  mainWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: moderateScale(AUTH_WRAPPER_TOP_PADDING),
  },
  centerBlock: {
    width: '100%',
    alignItems: 'center',
  },
});
